import React, { useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Menu, X, ShieldAlert, LayoutDashboard, ScrollText, CreditCard, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Topbar } from '@/components/Layout/Topbar';
import { useIsMobile } from '@/hooks/use-mobile';

/**
 * Shell for `/app/superadmin/*`. Mirrors the structure of `AppLayout` but
 * with a separate sidebar that only lists super-admin destinations and a
 * persistent "Modo super admin" pill so Sergio can always tell which mode
 * he's in. Routes for Plans / Users / Impersonate land in Waves 2A.2 and 2B
 * — left here as `aria-disabled` placeholders so the IA is visible.
 */

interface SuperAdminNavItem {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  disabled?: boolean;
}

const navItems: SuperAdminNavItem[] = [
  { to: '/app/superadmin', icon: LayoutDashboard, label: 'Panel' },
  { to: '/app/superadmin/audit', icon: ScrollText, label: 'Auditoría' },
  { to: '/app/superadmin/plans', icon: CreditCard, label: 'Planes' },
  { to: '/app/superadmin/users', icon: Users, label: 'Usuarios', disabled: true },
];

export const SuperAdminLayout: React.FC = () => {
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isMobile);

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside
        className={`${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} ${
          isMobile ? 'fixed inset-y-0 left-0 z-40' : 'relative'
        } w-64 bg-white border-r border-gray-200 transform transition-transform duration-200 ease-in-out flex flex-col`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-red-600" />
            <span className="font-semibold text-gray-900">Super admin</span>
          </div>
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Cerrar menú"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto p-3 space-y-1">
          {navItems.map(({ to, icon: Icon, label, disabled }) =>
            disabled ? (
              <span
                key={to}
                aria-disabled="true"
                className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-gray-400 cursor-not-allowed"
                title="Próximamente"
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
                <span className="ml-auto text-xs uppercase tracking-wide">Pronto</span>
              </span>
            ) : (
              <NavLink
                key={to}
                to={to}
                end={to === '/app/superadmin'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    isActive
                      ? 'bg-red-50 text-red-700 font-medium'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </NavLink>
            )
          )}
        </nav>

        <div className="p-3 border-t border-gray-200">
          <NavLink
            to="/app/dashboard"
            className="block text-xs text-gray-500 hover:text-gray-900"
          >
            ← Volver a la aplicación
          </NavLink>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <Topbar>
          {isMobile && (
            <Button
              variant="ghost"
              size="sm"
              className="mr-2"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Abrir menú"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700 border border-red-200">
            <ShieldAlert className="h-3 w-3" />
            Modo super admin
          </span>
        </Topbar>

        <main className="flex-1 overflow-auto p-2 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};
